"use client";

import { motion } from "framer-motion";

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const materialTransition = {
  duration: 0.5,
  ease: [0.2, 0, 0, 1] as const,
};

const materialEmphasizedTransition = {
  duration: 0.5,
  ease: [0.2, 0, 0, 1.2] as const,
};

type AnimateSectionProps = {
  children: React.ReactNode;
  className?: string;
  as?: "section" | "div";
  delay?: number;
};

export function AnimateSection({
  children,
  className,
  as = "section",
  delay = 0,
}: AnimateSectionProps) {
  const props = {
    initial: "initial" as const,
    whileInView: "animate" as const,
    viewport: { once: true, margin: "-80px" } as const,
    variants: fadeInUp,
    transition: { ...materialTransition, delay },
    className,
  };
  if (as === "section") {
    return <motion.section {...props}>{children}</motion.section>;
  }
  return <motion.div {...props}>{children}</motion.div>;
}

type StaggerContainerProps = {
  children: React.ReactNode;
  className?: string;
  as?: "div" | "ul" | "section";
};

export function StaggerContainer({
  children,
  className,
  as = "div",
}: StaggerContainerProps) {
  const props = {
    initial: "initial" as const,
    whileInView: "animate" as const,
    viewport: { once: true, margin: "-80px" } as const,
    variants: staggerContainer,
    transition: materialTransition,
    className,
  };
  if (as === "ul") return <motion.ul {...props}>{children}</motion.ul>;
  if (as === "section") return <motion.section {...props}>{children}</motion.section>;
  return <motion.div {...props}>{children}</motion.div>;
}

type StaggerItemProps = {
  children: React.ReactNode;
  className?: string;
  as?: "div" | "li" | "article";
};

export function StaggerItem({
  children,
  className,
  as = "div",
}: StaggerItemProps) {
  const props = {
    variants: staggerItem,
    transition: materialTransition,
    className,
  };
  if (as === "li") return <motion.li {...props}>{children}</motion.li>;
  if (as === "article") return <motion.article {...props}>{children}</motion.article>;
  return <motion.div {...props}>{children}</motion.div>;
}

export { fadeInUp, staggerContainer, staggerItem, materialTransition, materialEmphasizedTransition };
